"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils/utils";

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Stagger index — each step adds 80ms. */
  delay?: number;
  as?: "div" | "li" | "section" | "article" | "header" | "p";
  /** Needed where the revealed element is also a scroll anchor. */
  id?: string;
}

/**
 * The journal's one reveal.
 *
 * Every section on the blog enters the same way, so the page reads as one
 * document rather than a stack of separately animated widgets. Transform and
 * opacity only, fired once (`viewport.once`) so nothing re-animates on the way
 * back up, and framer-motion already respects `prefers-reduced-motion` for the
 * transform here.
 */
const Reveal = ({
  children,
  className,
  delay = 0,
  as = "div",
  id,
}: RevealProps) => {
  const Tag = motion[as];

  return (
    <Tag
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{
        duration: 0.7,
        ease: [0.22, 0.61, 0.36, 1],
        delay: delay * 0.08,
      }}
      id={id}
      className={cn(className)}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
